"use client";
import { Button, makeStyles, tokens } from "@fluentui/react-components";
import { ArrowLeft20Regular } from "@fluentui/react-icons";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { ButtonWithLoading } from "../components/ButtonWithLoading";
import { SearchConfigurationPicker } from "../components/search/SearchConfigurationPicker";
import {
  useGetGlobalSearchConfiguration,
  useUpdateGlobalSearchConfiguration,
} from "../hooks/useSearchConfiguration";
import { ClientRoutes } from "../models/ClientRoutes";
import { SearchFilterConfiguration } from "../models/SearchConfiguration";
import { useGetUserAccessLevel } from "../hooks/useGetUserAccessLevel";

const useClasses = makeStyles({
  root: {
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
    height: "100%",
    padding: tokens.spacingVerticalM,
    gap: tokens.spacingVerticalL,
  },
  header: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalM,
  },
  searchSettings: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalM,
    maxWidth: "350px",
  },
});

function SearchSettingsPage() {
  const classes = useClasses();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const accessLevel = useGetUserAccessLevel();

  if (accessLevel > -1 && accessLevel < 5) {
    navigate("/");
  }

  const { data: searchConfiguration, isLoading: searchConfigurationLoading } =
    useGetGlobalSearchConfiguration();

  const { mutateAsync: saveGlobalSearchConfiguration } =
    useUpdateGlobalSearchConfiguration();

  const [searchSettingsLoading, setSearchSettingsLoading] =
    useState<boolean>(false);

  const [newSearchConfiguration, setNewSearchConfiguration] = useState<
    SearchFilterConfiguration | undefined
  >();

  const handleSearchConfigurationSave = async () => {
    if (!newSearchConfiguration) {
      return;
    }
    setSearchSettingsLoading(true);
    await saveGlobalSearchConfiguration(newSearchConfiguration);
    setSearchSettingsLoading(false);
  };

  useEffect(() => {
    /* eslint-disable react-hooks/set-state-in-effect */
    setNewSearchConfiguration(searchConfiguration);
    /* eslint-enable react-hooks/set-state-in-effect */
  }, [searchConfiguration, setNewSearchConfiguration]);

  return (
    <div className={classes.root}>
      <div className={classes.header}>
        <Button
          icon={<ArrowLeft20Regular />}
          appearance="subtle"
          onClick={() => navigate(ClientRoutes.SystemAdmin)}
        />
        <h2>{t("globalSearchSettingsHeader")}</h2>
      </div>
      <div className={classes.searchSettings}>
        {!searchConfigurationLoading &&
          searchConfiguration &&
          newSearchConfiguration && (
            <>
              <SearchConfigurationPicker
                configuration={newSearchConfiguration}
                allowChaptersFilter
                onConfigurationChange={setNewSearchConfiguration}
              />
              <ButtonWithLoading
                text={t("saveGlobalSearchSettings")}
                isLoading={searchSettingsLoading}
                onClick={handleSearchConfigurationSave}
              />
            </>
          )}
      </div>
    </div>
  );
}

export default SearchSettingsPage;
